
<template>
  <v-container fluid>
    <v-layout>
      <v-flex xs12>
        <!-- Search Bar -->
        <v-layout row wrap align-center class="mt-4 mb-4" style="gap: 10px">
          <v-flex sm3 style="flex: 0 0 auto; min-width: 200px">
            <v-select
              v-model="searchBy"
              :items="searchOptions"
              item-text="text"
              item-value="value"
              outlined
              dense
            />
          </v-flex>

          <v-flex sm4 style="flex: 1; min-width: 250px">
            <v-text-field
              v-model="searchTermUpper"
              placeholder="Search"
              outlined
              dense
              clearable
              @keyup.enter="search"
            />
          </v-flex>

          <v-flex sm2 style="flex: 0 0 auto; min-width: 150px">
            <v-btn
              color="success"
              class="white--text"
              block
              style="height: 40px"
              :loading="loading"
              @click="search"
            >
              Search
            </v-btn>
          </v-flex>
        </v-layout>

        <!-- Data Table -->
        <v-data-table
          :headers="tableHeaders"
          :items="paginatedRows"
          :options.sync="tableOptions"
          :server-items-length="totalItems"
          :loading="loading"
          class="elevation-0"
        >
          <template v-slot:item="{ item }">
            <tr>
              <td>{{ item.organisationName }}</td>
              <td>{{ item.organisationCode }}</td>
              <td>{{ item.organisationTypeName }}</td>

              <td>
                <v-chip
                  :color="getStatusColor(item.statusName)"
                  text-color="white"
                  small
                >
                  {{ formatStatus(item.statusName) }}
                </v-chip>
              </td>

              <td class="text-center">
                <v-menu>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      class="cursor-pointer"
                      v-bind="attrs"
                      v-on="on"
                    >
                      fa-eye
                    </v-icon>
                  </template>

                  <v-list>
                    <v-list-item @click="openView(item)">
                      <v-list-item-title>View</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="canCreateOrg && canEditOrganisation(item)"
                      @click="openEdit(item)"
                    >
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="canAccessUsers(item)"
                      @click="addUsers(item.id)"
                    >
                      <v-list-item-title>View Users</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>

        <!-- View Dialog -->
        <v-dialog
          v-model="dialog"
          max-width="1200px"
          scrollable
        >
          <v-card>
            <v-toolbar color="white" flat class="mb-4">
              <v-btn icon @click="closeDialogs" color="success">
                <v-icon>fa-arrow-left</v-icon>
              </v-btn>
              <span class="ml-2">Back</span>
            </v-toolbar>

            <v-card-text>
              <!-- Loading Indicator -->
              <v-layout
                v-if="dialogLoading"
                align-center
                justify-center
                class="pa-5"
              >
                <v-progress-circular
                  indeterminate
                  color="success"
                  :size="50"
                />
              </v-layout>

              <!-- Details Card -->
              <v-card v-else class="mb-4 details-card">
                <v-card-text class="pa-4">
                  <div class="details-card-header-row mb-4">
                    <div>
                      <div class="detail-card-title">
                        {{ selectedOrganization.organisationName || 'Organization Details' }}
                      </div>
                    </div>
                    <div>
                      <v-chip
                        :color="getStatusColor(selectedOrganization.statusName || selectedOrganization.status?.statusName)"
                        text-color="white"
                        small
                      >
                        {{ formatStatus(selectedOrganization.statusName || selectedOrganization.status?.statusName || '-') }}
                      </v-chip>
                    </div>
                  </div>

                  <div class="detail-grid">
                    <div class="detail-column">
                      <div class="detail-row">
                        <span class="detail-label">Org Code</span>
                        <span class="detail-value">{{ selectedOrganization.organisationCode || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Org Name</span>
                        <span class="detail-value">{{ selectedOrganization.organisationName || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Org Type</span>
                        <span class="detail-value">{{ selectedOrganization.organisationTypeName || selectedOrganization.organisationType?.name || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Identification ID</span>
                        <span class="detail-value">{{ selectedOrganization.identificationDocumentID || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Contact Phone</span>
                        <span class="detail-value">{{ selectedOrganization.contactPersonPhone || '-' }}</span>
                      </div>
                    </div>

                    <div class="detail-column">
                      <div class="detail-row">
                        <span class="detail-label">Non-Checkoff Acc Name</span>
                        <span class="detail-value">{{ selectedRowDetails.settlement?.nonCheckoffAccountName || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Non-Checkoff Acc</span>
                        <span class="detail-value">{{ selectedRowDetails.settlement?.nonCheckoffAccountNumber || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Non-Checkoff Bank</span>
                        <span class="detail-value">{{ selectedRowDetails.settlement?.nonCheckoffBankName || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Currency</span>
                        <span class="detail-value">{{ selectedRowDetails.settlement?.nonCheckoffCurrency || selectedOrganization.currency || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Contact Name</span>
                        <span class="detail-value">{{ selectedOrganization.contactPersonName || '-' }}</span>
                      </div>
                    </div>

                    <div
                      class="detail-column"
                      v-if="!growerBrokerAccounts || growerBrokerAccounts.length === 0"
                    >
                      <div class="detail-row">
                        <span class="detail-label">Checkoff Acc Name</span>
                        <span class="detail-value">{{ selectedRowDetails.settlement?.checkoffAccountName || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Checkoff Acc</span>
                        <span class="detail-value">{{ selectedRowDetails.settlement?.checkoffAccount || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Checkoff Bank</span>
                        <span class="detail-value">{{ selectedRowDetails.settlement?.checkoffBank || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Currency</span>
                        <span class="detail-value">{{ selectedRowDetails.settlement?.checkoffCurrency || selectedOrganization.currency || '-' }}</span>
                      </div>
                    </div>

                    <div class="detail-column">
                      <div class="detail-row">
                        <span class="detail-label">Miller Code</span>
                        <span class="detail-value">{{ selectedRowDetails.settlement?.millerCode || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Miller Name</span>
                        <span class="detail-value">{{ selectedRowDetails.settlement?.millerName || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Warehouse Code</span>
                        <span class="detail-value">{{ selectedRowDetails.settlement?.warehouseCode || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Warehouse Name</span>
                        <span class="detail-value">{{ selectedRowDetails.settlement?.warehouseName || '-' }}</span>
                      </div>
                    </div>

                    <div class="detail-column">
                      <div class="detail-row">
                        <span class="detail-label">County</span>
                        <span class="detail-value">{{ selectedOrganization.county?.name || selectedOrganization.county || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Sub County</span>
                        <span class="detail-value">{{ selectedOrganization.subcounty?.name || selectedOrganization.subcounty || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Ward</span>
                        <span class="detail-value">{{ selectedOrganization.ward || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Village</span>
                        <span class="detail-value">{{ selectedOrganization.village || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Building</span>
                        <span class="detail-value">{{ selectedOrganization.building || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Street</span>
                        <span class="detail-value">{{ selectedOrganization.street || '-' }}</span>
                      </div>
                    </div>

                    <div class="detail-column">
                      <div class="detail-row">
                        <span class="detail-label">Address</span>
                        <span class="detail-value">{{ selectedOrganization.address || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Postal Code</span>
                        <span class="detail-value">{{ selectedOrganization.postalCode || '-' }}</span>
                      </div>
                      <div class="detail-row">
                        <span class="detail-label">Date of Incorporation</span>
                        <span class="detail-value">
                          {{ selectedOrganization.dateOfIncorporation ? formatChangeDate(selectedOrganization.dateOfIncorporation) : '-' }}
                        </span>
                      </div>
                    </div>

                    <!-- Broker Settlement Table -->
                    <div
                      v-if="growerBrokerAccounts && growerBrokerAccounts.length > 0"
                      class="broker-table-wrapper"
                    >
                      <h3 class="broker-table-title">Broker Settlement Information</h3>
                      <table class="broker-settlement-table">
                        <thead>
                          <tr>
                            <th>Broker Code</th>
                            <th>Checkoff Account Number</th>
                            <th>Checkoff Account Name</th>
                            <th>Swift Code</th>
                            <th>Currency</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(account, index) in growerBrokerAccounts"
                            :key="index"
                          >
                            <td>{{ account.brokerName || '-' }}</td>
                            <td>{{ account.accountNumber || '-' }}</td>
                            <td>{{ account.accountName || '-' }}</td>
                            <td>{{ account.swiftCode || '-' }}</td>
                            <td>{{ account.currency || '-' }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Approval Details -->
              <v-card v-if="!dialogLoading && !isEditMode" class="mb-4">
                <v-card-title class="text-subtitle1">Approval Details</v-card-title>

                <v-card-text>
                  <v-layout row wrap>
                    <v-flex xs12 sm6>
                      <div class="mb-2"><strong>Created By</strong></div>
                      <div class="pa-2 readonly-box">
                        {{ selectedOrganization.createdBy || '' }}
                      </div>
                    </v-flex>

                    <v-flex xs12 sm6>
                      <div class="mb-2"><strong>Date Created</strong></div>
                      <div class="pa-2 readonly-box">
                        {{ formatChangeDate(selectedOrganization.dateCreated) }}
                      </div>
                    </v-flex>
                  </v-layout>
                </v-card-text>

                <v-card-text>
                  <v-layout row wrap>
                    <v-flex xs12 sm6>
                      <div class="mb-2"><strong>Updated By</strong></div>
                      <div class="pa-2 readonly-box">
                        {{ selectedOrganization.updatedBy || '' }}
                      </div>
                    </v-flex>

                    <v-flex xs12 sm6>
                      <div class="mb-2"><strong>Date Updated</strong></div>
                      <div class="pa-2 readonly-box">
                        {{ formatChangeDate(selectedOrganization.dateUpdated) }}
                      </div>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Edit Organization Form -->
        <EditOrganizationForm
          :isOpen="isEditMode"
          :organisation="selectedRowDetails"
          :institutionTypes="institutionTypes"
          :currencies="currencies"
          :banks="banks"
          :county="county"
          :subcounty="subcounty"
          :ward="ward"
          :brokers="brokers"
          :millers="millers"
          :warehouse="warehouse"
          :taxtypes="taxtypes"
          :farmersocieties="farmersocieties"
          :growerBrokerAccounts="growerBrokerAccounts"
          :liabilityProviderLoans="liabilityProviderLoans"
          :loading="dialogLoading"
          :submitLoading="submitLoading"
          @close="closeDialogs"
          @update-complete="handleUpdateComplete"
          @show-snackbar="handleSnackbar"
        />
      </v-flex>
    </v-layout>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      top
      absolute
      attach="body"
      :timeout="3000"
      style="z-index: 12000;"
    >
      {{ message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios'
import config from '../../../config'
import store from '../../../store'
import EditOrganizationForm from './EditOrganizationForm.vue'

export default {
  name: 'OrganizationUpdateRequests',
  components: {
    EditOrganizationForm
  },

  data() {
    return {
      // Search
      searchBy: 'orgCode',
      searchTerm: '',
      searchOptions: [
        { text: 'Org Code', value: 'orgCode' }
      ],

      // Table
      tableHeaders: [
        { text: 'Name', value: 'organisationName' },
        { text: 'Org Code', value: 'organisationCode' },
        { text: 'Organisation Type', value: 'organisationTypeName' },
        { text: 'Status', value: 'statusName' },
        { text: 'Action', value: 'actions', sortable: false, align: 'center' }
      ],
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: []
      },
      lastFetchedOptionsKey: '',
      paginatedRows: [],
      totalItems: 0,
      loading: false,

      // Dialog / details
      dialog: false,
      isEditMode: false,
      dialogLoading: false,
      submitLoading: false,
      selectedRow: [],
      selectedRowId: null,
      selectedRowDetails: {},

      // Permissions
      permissions: [],

      // Snackbar
      request: '',
      action: '',
      message: '',
      snackbar: false,
      snackbarColor: 'success',

      // Lifecycle / optimization
      pageReady: false,
      masterDataLoaded: false,
      masterDataPromise: null,
      listRequestSeq: 0,
      detailsRequestSeq: 0,

      // Edit / master data
      institutionTypes: [],
      currencies: [],
      banks: [],
      county: [],
      subcounty: [],
      ward: [],
      brokers: [],
      millers: [],
      warehouse: [],
      taxtypes: [],
      farmersocieties: [],
      growerBrokerAccounts: [],
      liabilityProviderLoans: []
    }
  },

  computed: {
    canEditExternalUsers() {
      return this.hasPerm('CAN_UPDATE_EXTERNALUSERS')
    },
    canViewExternalUsers() {
      return this.hasPerm('CAN_VIEW_EXTERNALUSERS')
    },
    canCreateExternalUsers() {
      return this.hasPerm('CAN_CREATE_EXTERNALUSERS')
    },
    canCreateOrg() {
      return this.hasPerm('CAN_CREATE_ORGANISATION')
    },
    searchTermUpper: {
      get() {
        return this.searchTerm
      },
      set(value) {
        this.searchTerm = (value || '').toUpperCase()
      }
    },
    selectedOrganization() {
      const selected = this.selectedRowDetails || {}
      if (Object.keys(selected).length) return selected
      const row = this.paginatedRows.find(r => r.id === this.selectedRowId)
      return row || {}
    }
  },

  watch: {
    tableOptions: {
      deep: true,
      handler(newVal) {
        if (!this.pageReady) return

        const newKey = this.buildOptionsKey(newVal)
        if (newKey === this.lastFetchedOptionsKey) return

        this.fetchData()
      }
    },

    searchTerm(newVal, oldVal) {
      const current = (newVal || '').trim()
      const previous = (oldVal || '').trim()

      // If field is cleared, reload default list
      if (previous && !current) {
        this.tableOptions.page = 1
        this.fetchData()
      }
    }
  },

  mounted() {
    this.initializePage()
  },

  methods: {
    async initializePage() {
      this.initPermissions()

      // Mark page ready before first fetch so watcher remains consistent
      this.pageReady = true

      // Initial list load
      await this.fetchData()

      // Optional background preload: does not block first render
      this.preloadMasterDataInBackground()
    },

    initPermissions() {
      this.permissions = store?.state?.user?.roleId?.permissions || []
    },

    hasPerm(permission) {
      return this.permissions.includes(permission)
    },

    handleSnackbar({ message, color }) {
      this.message = message
      this.snackbarColor = color || 'success'
      this.snackbar = true
    },

    showError(message) {
      this.$store.dispatch('alert/displayAlert', {
        snackbar: true,
        color: 'error',
        message: message || 'Error occurred'
      })
    },

    buildOptionsKey(options = {}) {
      const page = options.page || 1
      const itemsPerPage = this.getSafeItemsPerPage(options.itemsPerPage)
      const sortBy = JSON.stringify(options.sortBy || [])
      const sortDesc = JSON.stringify(options.sortDesc || [])
      return `${page}|${itemsPerPage}|${sortBy}|${sortDesc}|${this.searchTerm || ''}`
    },

    getSafeItemsPerPage(value) {
      const parsed = Number(value)
      if (!Number.isFinite(parsed) || parsed <= 0) return 10
      return parsed
    },

    buildListUrl() {
      const pageNumber = Math.max(0, (this.tableOptions.page || 1) - 1)
      const pageSize = this.getSafeItemsPerPage(this.tableOptions.itemsPerPage)

      const params = new URLSearchParams({
        page: String(pageNumber),
        size: String(pageSize),
        query: this.searchTerm || ''
      })

      return `${config.serverURI}/organization/?${params.toString()}`
    },

    buildSearchUrl() {
      const pageNumber = Math.max(0, (this.tableOptions.page || 1) - 1)
      const pageSize = this.getSafeItemsPerPage(this.tableOptions.itemsPerPage)

      const params = new URLSearchParams({
        page: String(pageNumber),
        size: String(pageSize),
        searchTerm: this.searchTerm || ''
      })

      return `${config.serverURI}/organization/approved/searchOrganisation?${params.toString()}`
    },

    async handleUpdateComplete() {
  this.searchTerm = ''
  this.tableOptions.page = 1
  await this.fetchData()
},

    extractArrayFromResponse(response) {
      const data = response?.data

      if (Array.isArray(data)) return data
      if (Array.isArray(data?.data)) return data.data
      if (Array.isArray(data?.content)) return data.content

      if (data && typeof data === 'object') {
        const firstArray = Object.values(data).find(item => Array.isArray(item))
        if (firstArray) return firstArray
      }

      return []
    },

    extractPagedResponse(response) {
      const body = response?.data || {}
      return {
        items: body.data || body.content || [],
        totalItems: body.page?.totalElements || body.totalElements || 0
      }
    },

    async fetchData() {
      const requestId = ++this.listRequestSeq
      const start = performance.now()

      const optionsKey = this.buildOptionsKey(this.tableOptions)
      this.lastFetchedOptionsKey = optionsKey

      this.loading = true

      const url = this.buildListUrl()
      console.log('[OrganizationList] fetchData start', {
        requestId,
        url,
        options: { ...this.tableOptions },
        searchTerm: this.searchTerm
      })

      try {
        const response = await axios.get(url)

        // Ignore stale responses
        if (requestId !== this.listRequestSeq) {
          console.warn('[OrganizationList] stale response ignored', { requestId })
          return
        }

        if (response.status !== 200) {
          this.showError(response?.data?.message || 'Failed to load organizations')
          return
        }

        const { items, totalItems } = this.extractPagedResponse(response)
        this.paginatedRows = Array.isArray(items) ? items : []
        this.totalItems = totalItems

        console.log(
          `[OrganizationList] fetchData complete in ${(performance.now() - start).toFixed(2)} ms`,
          { count: this.paginatedRows.length, totalItems: this.totalItems }
        )
      } catch (error) {
        if (requestId !== this.listRequestSeq) return

        console.error('[OrganizationList] fetchData error', error)
        this.showError(error?.response?.data?.message || 'Error fetching organizations')
      } finally {
        if (requestId === this.listRequestSeq) {
          this.loading = false
        }
      }
    },

    async search() {
      this.tableOptions.page = 1

      // If no search term, reload full list
      if (!this.searchTerm || !this.searchTerm.trim()) {
        await this.fetchData()
        return
      }

      const requestId = ++this.listRequestSeq
      const start = performance.now()
      const url = this.buildSearchUrl()

      this.loading = true

      console.log('[OrganizationList] search start', {
        requestId,
        url,
        searchTerm: this.searchTerm
      })

      try {
        const response = await axios.get(url)

        if (requestId !== this.listRequestSeq) {
          console.warn('[OrganizationList] stale search response ignored', { requestId })
          return
        }

        if (response.status !== 200) {
          this.showError(response?.data?.message || 'Search failed')
          return
        }

        const { items, totalItems } = this.extractPagedResponse(response)
        this.paginatedRows = Array.isArray(items) ? items : []
        this.totalItems = totalItems

        console.log(
          `[OrganizationList] search complete in ${(performance.now() - start).toFixed(2)} ms`,
          { count: this.paginatedRows.length, totalItems: this.totalItems }
        )
      } catch (error) {
        if (requestId !== this.listRequestSeq) return

        console.error('[OrganizationList] search error', error)
        this.showError(error?.response?.data?.message || 'Error occurred during search')
      } finally {
        if (requestId === this.listRequestSeq) {
          this.loading = false
        }
      }
    },

    closeDialogs() {
      this.dialog = false
      this.isEditMode = false
      this.dialogLoading = false
    },

    addUsers(organizationId) {
      this.$router.push(`/organization/${organizationId}/users`)
    },

    async fetchOrganizationDetails(rowId) {
      const requestId = ++this.detailsRequestSeq
      const start = performance.now()

      const url = `${config.serverURI}/organization/organisationdetails?id=${encodeURIComponent(rowId)}`
      console.log('[OrganizationDetails] fetch start', { requestId, url })

      const response = await axios.get(url)

      // Ignore stale response
      if (requestId !== this.detailsRequestSeq) {
        console.warn('[OrganizationDetails] stale response ignored', { requestId })
        return null
      }

      console.log(
        `[OrganizationDetails] fetch complete in ${(performance.now() - start).toFixed(2)} ms`
      )

      const data = response?.data?.data || response?.data?.content || response?.data

      if (Array.isArray(data) && data.length > 0) {
        return data[0]
      }

      if (data && typeof data === 'object') {
        return data
      }

      return null
    },

    async openView(row) {
      this.selectedRowId = row.id
      this.selectedRowDetails = { ...row }
      this.growerBrokerAccounts = []
      this.dialog = true
      this.isEditMode = false
      this.dialogLoading = true

      try {
        const details = await this.fetchOrganizationDetails(row.id)

        if (details) {
          this.selectedRowDetails = {
            ...this.selectedRowDetails,
            ...details
          }
          this.growerBrokerAccounts = details.growerBrokerAccounts || []
        }
      } catch (error) {
        console.error('[OrganizationDetails] view error', error)
        this.showError(error?.response?.data?.message || 'Error fetching request details')
      } finally {
        this.dialogLoading = false
      }
    },

    async openEdit(row) {
      this.selectedRowId = row.id
      this.selectedRowDetails = { ...row }
      this.growerBrokerAccounts = []
      this.isEditMode = true
      this.dialogLoading = true

      try {
        // Load reference/master data first (cached after first successful call)
        await this.loadMasterData()

        const details = await this.fetchOrganizationDetails(row.id)

        if (details) {
          this.selectedRowDetails = {
            ...this.selectedRowDetails,
            ...details
          }
          this.growerBrokerAccounts = details.growerBrokerAccounts || []
        }
      } catch (error) {
        console.error('[OrganizationDetails] edit error', error)
        this.isEditMode = false
        this.showError(error?.response?.data?.message || 'Error fetching request details')
      } finally {
        this.dialogLoading = false
      }
    },

    preloadMasterDataInBackground() {
      if (this.masterDataLoaded || this.masterDataPromise) return

      // Do not block initial table render
      setTimeout(() => {
        this.loadMasterData().catch(error => {
          console.warn('[MasterData] background preload failed', error)
        })
      }, 0)
    },

    async loadMasterData(force = false) {
      if (!force && this.masterDataLoaded) {
        console.log('[MasterData] already loaded, skipping')
        return
      }

      // Reuse in-flight promise to avoid duplicate parallel requests
      if (!force && this.masterDataPromise) {
        console.log('[MasterData] using existing in-flight promise')
        return this.masterDataPromise
      }

      const start = performance.now()
      console.log('[MasterData] load start', { force })

      const endpoints = {
        institutionTypes: axios.get(`${config.serverURI}/instution/get`),
        currencies: axios.get(`${config.serverURI}/organization/currency`),
        banks: axios.get(`${config.serverURI}/bank`),
        county: axios.get(`${config.serverURI}/county/findAll`),
        brokers: axios.get(`${config.serverURI}/organization/get-marketers`),
        millers: axios.post(`${config.serverURI}/organization/getMillers`),
        warehouse: axios.post(`${config.serverURI}/organization/getWarehouse`),
        taxtypes: axios.get(`${config.serverURI}/taxtypes/findAll`)
      }

      this.masterDataPromise = Promise.allSettled(Object.values(endpoints))
        .then(results => {
          const keys = Object.keys(endpoints)

          results.forEach((result, index) => {
            const key = keys[index]

            if (result.status === 'fulfilled') {
              this[key] = this.extractArrayFromResponse(result.value)
            } else {
              console.error(`[MasterData] failed loading ${key}`, result.reason)
              this[key] = []
            }
          })

          this.masterDataLoaded = true

          console.log(
            `[MasterData] load complete in ${(performance.now() - start).toFixed(2)} ms`,
            {
              institutionTypes: this.institutionTypes.length,
              currencies: this.currencies.length,
              banks: this.banks.length,
              county: this.county.length,
              brokers: this.brokers.length,
              millers: this.millers.length,
              warehouse: this.warehouse.length,
              taxtypes: this.taxtypes.length
            }
          )
        })
        .catch(error => {
          console.error('[MasterData] unexpected load error', error)
          throw error
        })
        .finally(() => {
          this.masterDataPromise = null
        })

      return this.masterDataPromise
    },

    canEditOrganisation(item) {
      const status = item?.statusName
      return ['APPROVED', 'PENDING_APPROVAL', 'BUYER_PENDING_REG'].includes(status)
    },

    canAccessUsers(item) {
      return (
        (this.canViewExternalUsers || this.canCreateExternalUsers) &&
        item?.statusName === 'APPROVED'
      )
    },

    formatChangeDate(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleString()
    },

    getStatusColor(status) {
      if (!status) return '#999'

      const value = String(status).toLowerCase()
      if (value.includes('pending')) return '#ff9800'
      if (value.includes('approved') || value.includes('completed')) return '#4caf50'
      if (value.includes('rejected') || value.includes('declined')) return '#f44336'
      return '#2196f3'
    },

    formatStatus(status) {
      if (!status) return ''
      return String(status)
        .replace(/_/g, ' ')
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }
  }
}
</script>

<style scoped>
/* ===== Page layout ===== */
.v-card {
  border-radius: 8px;
}

.v-card__title {
  font-weight: 600;
  font-size: 18px;
}

.v-card__text {
  padding-top: 8px;
}

/* ===== Search bar ===== */
.v-text-field input {
  font-size: 14px;
}

.v-text-field .v-label {
  font-size: 13px;
}

/* ===== Data table ===== */
.v-data-table {
  border-radius: 8px;
}

.v-data-table thead th {
  font-weight: 600;
  font-size: 13px;
  color: #555;
  background-color: #f5f7f8;
}

.v-data-table tbody td {
  font-size: 13px;
  color: #333;
}

.v-data-table tbody tr:hover {
  background-color: #f9fbfa;
}

/* ===== Status styling ===== */
.v-chip {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
}

/* ===== Action menu ===== */
.actions {
  min-width: 32px;
}

.v-list-item__title {
  font-size: 13px;
}

/* ===== Dialog ===== */
.v-dialog .v-card {
  border-radius: 10px;
}

.v-dialog .headline {
  font-size: 18px;
  font-weight: 600;
}

.details-card {
  border: 2px solid #7bc243;
  border-radius: 10px;
}

.details-card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dfe8df;
  padding-bottom: 10px;
}

.detail-card-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: #1a3d14;
}

.detail-card-subtitle {
  color: #55605d;
  font-size: 0.9rem;
  margin-top: 4px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 24px;
}

.detail-column {
  display: flex;
  flex-direction: column;
}

.detail-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-label {
  color: #6c6c6c;
  font-weight: 700;
  font-size: 0.78rem;
  margin-bottom: 4px;
  text-transform: none;
}

.detail-value {
  color: #1f3e2d;
  font-weight: 500;
  font-size: 0.95rem;
  line-height: 1.4;
  white-space: pre-wrap;
}

.details-card .v-chip {
  min-width: 100px;
}

/* ===== Form fields (readonly look) ===== */
.v-text-field input[readonly] {
  background-color: #f8f9fa;
}

/* ===== Broker table ===== */
.details-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.details-table th {
  text-align: left;
  background: #f5f7f8;
  padding: 8px;
  font-weight: 600;
}

.details-table td {
  padding: 8px;
  border-bottom: 1px solid #eee;
}

.broker-table-wrapper {
  grid-column: 1 / -1;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #dfe8df;
}

.broker-table-title {
  font-weight: 700;
  font-size: 1rem;
  color: #1a3d14;
  margin-bottom: 12px;
}

.broker-settlement-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  border: 1px solid #ddd;
}

.broker-settlement-table thead th {
  background-color: #f5f7f8;
  padding: 12px;
  font-weight: 600;
  text-align: left;
  color: #555;
  border-bottom: 2px solid #ddd;
}

.broker-settlement-table tbody td {
  padding: 12px;
  border-bottom: 1px solid #eee;
  color: #1f3e2d;
}

.broker-settlement-table tbody tr:hover {
  background-color: #f9fbfa;
}

.broker-settlement-table .text-center {
  text-align: center;
  color: #999;
}


/* ===== Pagination ===== */
.paginationtext {
  font-size: 13px;
  color: #666;
}

/* ===== Buttons ===== */
.v-btn.primary {
  background-color: #004d3d !important;
}

.v-btn.secondary {
  background-color: #e0e0e0 !important;
  color: #333 !important;
}
</style>
